import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ChatMessage } from './AIAssistant';
import { LOCAL_STORAGE_KEYS } from 'utils/constants';
import useSendFeedback from './crud/useSendFeedback';

interface MessageFooterProps {
  message: ChatMessage;
  index: number;
  messages: ChatMessage[];
  feedbackState: { [key: number]: 'liked' | 'disliked' | null };
  handleThumbsUp: (index: number) => void;
  handleThumbsDown: (index: number) => void;
  setSnackbar: React.Dispatch<
    React.SetStateAction<{ open: boolean; message: string; isError: boolean }>
  >;
  setFeedbackUuid: React.Dispatch<React.SetStateAction<string | null>>;
  handleOpenFeedbackModal: () => void;
}

const copyToClipboard = (
  text: string,
  setSnackbar: React.Dispatch<
    React.SetStateAction<{ open: boolean; message: string; isError: boolean }>
  >
) => {
  navigator.clipboard.writeText(text);
  setSnackbar({ open: true, message: 'Message copied to clipboard', isError: false });
};

const MessageFooter: React.FC<MessageFooterProps> = ({
  message,
  index,
  messages,
  feedbackState,
  handleThumbsUp,
  handleThumbsDown,
  setSnackbar,
  setFeedbackUuid,
  handleOpenFeedbackModal
}) => {
  const sendFeedbackMutation = useSendFeedback({
    onSuccess: (data, variables) => {
      if (!variables.payload.is_good) {
        setFeedbackUuid(data.uuid);
        handleOpenFeedbackModal();
      } else {
        setSnackbar({ open: true, message: 'Feedback submitted successfully!', isError: false });
      }
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to send feedback. Please try again.',
        isError: true
      });
    }
  });

  const handleFeedback = (isGood: boolean) => {
    const sessionId = localStorage.getItem(LOCAL_STORAGE_KEYS.aiChatSessionId) || '';
    const userMessage = messages[index - 1]?.message;
    sendFeedbackMutation.mutate({
      payload: {
        is_good: isGood,
        session_id: sessionId,
        chat: `'user':'${userMessage}' 'assistant':'${message.message}'`
      }
    });
  };

  const handleThumbsUpClick = () => {
    handleThumbsUp(index);
    handleFeedback(true);
  };

  const handleThumbsDownClick = () => {
    handleThumbsDown(index);
    handleFeedback(false);
  };

  return (
    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={0.5} mr={2}>
      <Tooltip title="Copy message" arrow>
        <IconButton
          sx={{ cursor: 'pointer', fontSize: 20, m: 0 }}
          onClick={() => copyToClipboard(message.message, setSnackbar)}
        >
          <ContentCopyIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Good response" arrow>
        <IconButton
          sx={{
            cursor: 'pointer',
            mr: 1,
            fontSize: 20,
            m: 0,
            '&.Mui-disabled': {
              color: 'inherit',
              pointerEvents: 'auto'
            }
          }}
          onClick={handleThumbsUpClick}
          disabled={feedbackState[index] === 'liked'}
        >
          {feedbackState[index] === 'liked' ? (
            <ThumbUpIcon style={{ fontSize: 18 }} />
          ) : (
            <ThumbUpOutlinedIcon style={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Bad response" arrow>
        <IconButton
          sx={{
            cursor: 'pointer',
            fontSize: 20,
            m: 0,
            '&.Mui-disabled': {
              color: 'inherit',
              pointerEvents: 'auto'
            }
          }}
          onClick={handleThumbsDownClick}
          disabled={feedbackState[index] === 'disliked'}
        >
          {feedbackState[index] === 'disliked' ? (
            <ThumbDownIcon style={{ fontSize: 18 }} />
          ) : (
            <ThumbDownOutlinedIcon style={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default MessageFooter;
